import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ManagementContainerModel, ManagementShipmentModel, ManagementVoyageModel } from '@data-layer/models/management';
import { enumShipmentState, enumVoyageStatus, enumVoyageType } from '@data-layer/enums/lcl';
import { IDefaultSelect } from '@interfaces/default-select';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { enumLclTrackingStates } from '@enum/enumLclTrackingStates';
import { precisionRound } from '@helpers/data-transform.helper';
import { VoyageTotalsModel } from '@app/modules/management/voyages-management/models';

@Component({
  selector: 'view-voyage',
  templateUrl: './view-voyage.component.html',
  styleUrls: ['./view-voyage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewVoyageComponent implements OnInit, OnChanges {
  @Input() public voyage: ManagementVoyageModel;
  @Input() public containers: ManagementContainerModel[];
  @Input() public quotesCount: number;
  @Input() public isListItem: boolean;
  @Input() public isShipmentItem: boolean;
  @Input() public isEditable: boolean = true;
  @Output() public editVoyage: EventEmitter<void> = new EventEmitter<void>();
  @Output() public changeStatus: EventEmitter<string> = new EventEmitter<string>();
  @Output() public changeModalState: EventEmitter<string> = new EventEmitter<string>();
  @Output() public addManualQuoteOpen: EventEmitter<void> = new EventEmitter<void>();

  public enumVoyageStatus: typeof enumVoyageStatus = enumVoyageStatus;
  public statusItems: IDefaultSelect[];
  public voyageTotals: { atWarehouse: VoyageTotalsModel; total: VoyageTotalsModel };

  constructor(private translateService: TranslateService, private router: Router) {}

  public ngOnInit(): void {
    this.statusItems = Object.keys(enumVoyageStatus)
      .filter((item: string) => Number(item) > -1 && Number(item) !== enumVoyageStatus.OPTIMIZED) // see #6988
      .map((item: any) => ({
        id: item,
        label: this.translateService.instant('VOYAGE_STATUS.' + enumVoyageStatus[item]),
        value: item,
      }));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.containers?.firstChange && changes?.containers?.currentValue) {
      this.voyageTotals = { atWarehouse: this.buildTotals(true), total: this.buildTotals() };
    }
  }

  public routeSegment(description: string): string {
    return description?.split(';')?.[0];
  }

  public get isColoading(): boolean {
    return this.voyage?.voyageType === enumVoyageType.COLOADING;
  }

  public get isCombinedPricing(): boolean {
    return !!this.voyage?.combinedPricelist;
  }

  public redirectToVoyage(): void {
    if (this.isShipmentItem) {
      this.router.navigate(['/voyages-management', this.voyage.id]);
    }
  }

  public redirectToPriceList(id: number, isCombined?: boolean): void {
    if (isCombined) {
      this.router.navigate(['/price-list-combined-management', id]);
    } else {
      this.router.navigate(['/price-list-management', id]);
    }
  }

  public redirectToCfsView(agentId: number, storageId: number): void {
    this.router.navigate(['/container-freight-storages-management', agentId, storageId, 'view']);
  }

  public get stateBackground(): string {
    switch (this.voyage?.status) {
      case enumVoyageStatus.DRAFT:
        return 'black';
      default:
        return 'green';
    }
  }

  public get statusName(): string {
    return this.translateService.instant('VOYAGE_STATUS.' + enumVoyageStatus[this.voyage?.status]);
  }

  private getTotalVolume(atWarehouse?: boolean): number {
    const shipments: ManagementShipmentModel[] = atWarehouse
      ? this.allShipments.filter((item: ManagementShipmentModel) => this.isShipmentAtWarehouse(item.trackingState))
      : this.allShipments;

    return precisionRound(
      shipments.reduce((total: number, currentShipment: ManagementShipmentModel) => {
        return !this.isShipmentCanceled(currentShipment.state)
          ? total + currentShipment?.aggregatedStatistics?.realMeasurements?.totalVolume
          : total;
      }, 0),
    );
    // return this.shipments.reduce((total: number, item: ManagementShipmentModel) => total + item.aggregatedStatistics.totalVolume, 0);
  }

  private getTotalPackages(atWarehouse?: boolean): number {
    const shipments: ManagementShipmentModel[] = atWarehouse
      ? this.allShipments.filter((item: ManagementShipmentModel) => this.isShipmentAtWarehouse(item.trackingState))
      : this.allShipments;

    return shipments.reduce((total: number, currentShipment: ManagementShipmentModel) => {
      return !this.isShipmentCanceled(currentShipment.state) ? total + currentShipment?.aggregatedStatistics?.totalPackages : total;
    }, 0);
    // return this.shipments.reduce((total: number, item: ManagementShipmentModel) => total + item.aggregatedStatistics.totalPackages, 0);
  }

  private getTotalWeight(atWarehouse?: boolean): number {
    const shipments: ManagementShipmentModel[] = atWarehouse
      ? this.allShipments.filter((item: ManagementShipmentModel) => this.isShipmentAtWarehouse(item.trackingState))
      : this.allShipments;

    return precisionRound(
      shipments.reduce((total: number, currentShipment: ManagementShipmentModel) => {
        return !this.isShipmentCanceled(currentShipment.state)
          ? total + currentShipment?.aggregatedStatistics?.realMeasurements?.totalWeight
          : total;
      }, 0),
    );
    // .reduce((total: number, item: ManagementShipmentModel) => total + item.aggregatedStatistics.totalWeight, 0);
  }

  private getTotalShipmentsCount(atWarehouse?: boolean): number {
    const shipments: ManagementShipmentModel[] = atWarehouse
      ? this.allShipments.filter((item: ManagementShipmentModel) => this.isShipmentAtWarehouse(item.trackingState))
      : this.allShipments;

    return shipments.reduce((total: number, currentShipment: ManagementShipmentModel) => {
      return !this.isShipmentCanceled(currentShipment.state) ? total + 1 : total;
    }, 0);
  }

  private isShipmentCanceled(state: enumShipmentState): boolean {
    return state === enumShipmentState.CANCELED;
  }

  private isShipmentAtWarehouse(trackingState: enumLclTrackingStates): boolean {
    return trackingState >= enumLclTrackingStates.AT_ORIGIN_WAREHOUSE;
  }

  private getContainersCount(atWarehouse?: boolean): number {
    return this.containers?.length;
  }

  private buildTotals(atWarehouse?: boolean): VoyageTotalsModel {
    return {
      shipmentsCount: this.getTotalShipmentsCount(atWarehouse),
      containersCount: this.getContainersCount(atWarehouse),
      packagesCount: this.getTotalPackages(atWarehouse),
      volume: this.getTotalVolume(atWarehouse),
      weight: this.getTotalWeight(atWarehouse),
    };
  }

  private get allShipments(): ManagementShipmentModel[] {
    return this.containers?.reduce((allShipments: ManagementShipmentModel[], container: ManagementContainerModel) => {
      return [...allShipments, ...container.shipments];
    }, []);
  }
}
